import { lazy, Suspense, useState, type ReactNode } from 'react';
import { Loader2, Smile } from 'lucide-react';
// Types only: a value import would pull the whole picker into the main bundle.
import type { EmojiStyle, Theme } from 'emoji-picker-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

// The picker ships every emoji's name and keywords for search, so it loads
// only when an admin first opens it.
const Picker = lazy(() => import('emoji-picker-react'));

/**
 * Full emoji picker (categories, search, skin tones, recently used) in the
 * style of chat apps. Native emoji, so what the admin picks is exactly the
 * character creators see.
 */
export function EmojiPanel({ onPick, width = 340, height = 400 }: { onPick: (emoji: string) => void; width?: number; height?: number }) {
  const dark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center" style={{ width, height }}>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      }
    >
      <Picker
        onEmojiClick={(data) => onPick(data.emoji)}
        emojiStyle={'native' as EmojiStyle}
        theme={(dark ? 'dark' : 'light') as Theme}
        lazyLoadEmojis
        autoFocusSearch
        searchPlaceHolder="Search emoji"
        previewConfig={{ showPreview: false }}
        width={width}
        height={height}
      />
    </Suspense>
  );
}

/**
 * A smiley button that opens the picker. `onOpen` runs before it opens, so a
 * text field can remember its cursor before focus moves into the picker.
 */
export function EmojiButton({
  onPick,
  onOpen,
  keepOpen,
  onOpenChange,
  className,
  children,
  label = 'Insert emoji',
}: {
  onPick: (emoji: string) => void;
  onOpen?: () => void;
  /** Keep the picker open after a pick, for adding several in a row. */
  keepOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  children?: ReactNode;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) onOpen?.();
        setOpen(next);
        onOpenChange?.(next);
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={label}
              className={className ?? 'h-8 w-8 text-muted-foreground'}
              // Keep focus (and the cursor) in the text field when clicked.
              onMouseDown={(e) => e.preventDefault()}
            >
              {children ?? <Smile className="h-4 w-4" />}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-auto border-0 p-0 shadow-xl">
        <EmojiPanel
          onPick={(emoji) => {
            onPick(emoji);
            if (!keepOpen) { setOpen(false); onOpenChange?.(false); }
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
